import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  DateFormatConstants,
  FareCalculationNotFoundException,
  FileData,
  PhoneNumberUtil,
  RideRequestNoLongerAvailableException,
  ServiceException,
  TextDetectionResult,
  UzurvDataSource,
  isNullOrUndefined
} from '@uzurv/core';

// eslint-disable-next-line import/order
import { AuthDataService, UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { CountryCode } from 'libphonenumber-js';
import moment from 'moment-timezone';
import { Observable, Subject, of, throwError, timer } from 'rxjs';
import { catchError, filter, map, mergeMapTo } from 'rxjs/operators';
import {
  Address,
  Banner,
  CancellationReason,
  ChatMessage,
  Driver,
  DriverAnnualRideExecutionData,
  DriverBank,
  DriverEarnings,
  DriverStatus,
  DriverSuspension,
  InsurancePolicy,
  InterestedDriver,
  JourneyDetail,
  JourneyInterestedDriver,
  MiniDriver,
  MiniRider,
  OfflineRouteNode,
  OrganizationPaymentMethod,
  PaginatedJourneyDetailList,
  PaymentAccount,
  PositionUpdate,
  Release,
  Reservation,
  ReservationRouteSummary,
  ReservationSignature,
  ReservationStatusUpdate,
  RideDetail,
  RideRequestFeedItem,
  RideRequestInfo,
  RiderFare,
  RiderSignature,
  RuleValue,
  SupportMessage,
  TncLicense,
  TollReimbursement,
  TransitProgramVehicleInspectionItem,
  User,
  Vehicle,
  VehicleInspection
} from '../../models';

import { BannerService } from '../banner/banner.service';

@Injectable({
  providedIn: 'root'
})
export class DriverService extends UzurvRestService {
  readonly resourceUrl = 'drivers/';

  // Endpoint RegExps
  static readonly LIST_RESERVATIONS_URL_REGEX: RegExp = /drivers\/[0-9]*\/reservations\/?$/;
  static readonly RESERVATION_BY_ID_URL_REGEX: RegExp = /drivers\/[0-9]*\/reservations\/([0-9]*)\/?$/;
  static readonly TOLL_REIMBURSEMENT_URL_REGEX: RegExp = /drivers\/[0-9]*\/reservations\/([0-9]*)\/tollReimbursements\/?$/;
  static readonly CURRENT_DRIVER_STATUS_URL_REGEX: RegExp = /drivers\/[0-9]*\/currentStatus\/?$/;
  static readonly LIST_CANCELLATION_REASONS_URL_REGEX: RegExp = /reservations\/cancellationReasons\/?$/;
  static readonly RIDE_REQUEST_INFO_URL_REGEX: RegExp = /drivers\/[0-9]*\/rideRequests\/([0-9]*)\/rideRequestInfo\/?$/;
  static readonly RESERVATION_PHONE_CALLS_URL_REGEX: RegExp = /drivers\/[0-9]*\/reservations\/([0-9]*)\/phoneCalls\/?$/;
  static readonly RESERVATION_RIDER_POSITION_URL_REGEX: RegExp = /drivers\/[0-9]*\/reservations\/([0-9]*)\/riderPosition\/?$/;
  static readonly TRANSIT_PROGRAMS_URL_REGEX: RegExp = /transitPrograms\/?$/;
  static readonly RULE_VALUES_URL_REGEX: RegExp = /ruleValues\/?$/;
  static readonly GET_RESERVATION_BY_ID: RegExp = /drivers\/[0-9]*\/reservations\/([0-9]*)\/?$/;
  static readonly RESERVATION_STATUS_UPDATE_URL_REGEX: RegExp = /drivers\/[0-9]*\/reservations\/([0-9]*)\/reservationStatus/;
  static readonly DRIVER_POSITION_UPDATE_URL_REGEX: RegExp = /drivers\/[0-9]*\/driverPosition/;
  static readonly SYNC_QUEUED_ROUTE_NODES_URL_REGEX: RegExp = /drivers\/[0-9]*\/queuedRouteNodes/;
  static readonly RIDER_FARE_URL_REGEX: RegExp = /drivers\/[0-9]*\/reservations\/[0-9]*\/riderFares\/?(?:\?multi=true)?/;

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer,
    private bannerService: BannerService,
    private authDataService: AuthDataService
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /*****************  Search ***********************/

  /**
   * Finds a driver by their ID
   *
   * @param driverId
   * @permission driver
   */
  findDriverById(driverId: number): Observable<Driver> {
    return this.get(this.resourceUrl + driverId).pipe(map(response => Deserialize(response, Driver)));
  }

  /**
   * Search a driver by arbitrary means. Will match on name, username, ID, etc.
   *
   * @param searchString
   * @param pageNumber
   * @param pageSize
   * @permission driver_search
   */
  searchForDriver(searchString: string, pageNumber = 0, pageSize = 20): Observable<Array<Driver>> {
    return this.get('search/drivers/', {
      searchString: searchString,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, Driver)));
  }

  /*****************  Help ***********************/

  /**
   * Creates a support message attached to the user by ID
   *
   * @param id
   * @param supportMessage
   */
  createSupportMessage(id: number, supportMessage: SupportMessage): Observable<any> {
    return this.post(`${this.resourceUrl}${id}/supportMessages/`, supportMessage);
  }

  /**************** Driver Status ***************/

  /**
   * Get the driver's current status
   *
   * @param {number} driverId
   * @permission driver
   * @returns {Observable<DriverStatus>}
   * @memberof DriverService
   */
  getCurrentDriverStatus(driverId: number): Observable<DriverStatus> {
    if (isNullOrUndefined(driverId)) {
      return throwError(new ServiceException('Driver Cannot Be Null'));
    }

    return this.get(`${this.resourceUrl}${driverId}/currentStatus/`).pipe(map(response => Deserialize(response, DriverStatus)));
  }

  /*****************  Driver Bank ***********************/

  /**
   * Retrieves a driver's bank by their ID
   *
   * @param driverId
   * @permission driver
   */
  getDriverBankByDriverId(driverId: number): Observable<DriverBank> {
    return this.get(`${this.resourceUrl}${driverId}/driverBank`).pipe(map(response => Deserialize(response, DriverBank)));
  }

  /*****************  Driver Location ***********************/

  /**
   * Returns a MiniDriver object for the requested driver
   *
   * @param userId ID of the driver
   * @param transitProgramId ID of the transit program you are scoped to
   */
  getDriverLocation(userId: number, transitProgramId: number): Observable<MiniDriver> {
    return this.get(`transitPrograms/${transitProgramId}/drivers/${userId}/driverPosition`).pipe(
      map(response => Deserialize(response, MiniDriver))
    );
  }

  /**
   * Subscribes to a feed of driver location by driver ID. Every {interval} seconds, the current
   * location of the driver is returned to the subscriber
   *
   * @param userId
   * @param transitProgramId
   * @param until a Subject<boolean> that emits true when the service should kill
   * the underlying poll. Set this up and tear it down on the component level to close network
   * leaks
   * @param interval the time in seconds between polling requests
   */
  trackDriverLocation(userId: number, transitProgramId: number, until: Subject<boolean>, interval?: number): Observable<MiniDriver> {
    return timer(0, interval).pipe(
      filter(() => this.authDataService.isLoggedIn),
      mergeMapTo(this.getDriverLocation(userId, transitProgramId))
    );
  }

  /**
   * PUTs the driver's location by ID, latitude, and longitude. Will default to
   * the current timestamp, or one can be supplied
   *
   *
   * @param driverId
   * @param latitude
   * @param longitude
   * @param timestamp
   * @param deviceInstallationId
   * @permission driver
   */
  updateDriverPosition(
    driverId: number,
    latitude: number,
    longitude: number,
    timestamp: Date,
    deviceInstallationId: number
  ): Observable<null> {
    const positionUpdate = new PositionUpdate();
    positionUpdate.personId = driverId;
    positionUpdate.latitude = latitude;
    positionUpdate.longitude = longitude;
    positionUpdate.timestamp = timestamp;
    positionUpdate.deviceInstallationId = deviceInstallationId;

    return this.put(`${this.resourceUrl}${driverId}/driverPosition`, Serialize(positionUpdate));
  }

  /*****************  Driver ***********************/

  /**
   * Creates a new user with the driver role
   *
   * @param user
   * @permission driver
   */
  registerDriver(user: User): Observable<User> {
    let countryCode: CountryCode = PhoneNumberUtil.getCountryCode(user.person.phoneNumber);

    if (isNullOrUndefined(countryCode)) {
      countryCode = 'US';
    }

    const hasValidPhoneNumber = PhoneNumberUtil.isValidPhoneNumber(user.person.phoneNumber, countryCode);

    if (!hasValidPhoneNumber) {
      return throwError(new ServiceException('Invalid Phone Number'));
    }

    user.person.phoneNumber = PhoneNumberUtil.formatPhoneNumberForRest(user.person.phoneNumber, countryCode);

    return this.post<User>(this.resourceUrl, Serialize(user)).pipe(map(response => Deserialize(response, User)));
  }

  /**
   * Updates an existing driver
   *
   * @param driverId
   * @param patchDictionary
   * @permission driver
   *
   * TODO: replace object for a more appropriate type.
   */
  patchDriver(driverId: number, patchDictionary: object): Observable<Driver> {
    if (!patchDictionary.hasOwnProperty('id')) {
      patchDictionary['id'] = driverId;
    }

    if (!isNullOrUndefined(patchDictionary['phoneNumber'])) {
      let countryCode: CountryCode = PhoneNumberUtil.getCountryCode(patchDictionary['phoneNumber']);

      if (isNullOrUndefined(countryCode)) {
        countryCode = 'US';
      }

      patchDictionary['phoneNumber'] = PhoneNumberUtil.formatPhoneNumberForRest(patchDictionary['phoneNumber'], countryCode);
    }

    return this.patch<Driver>(`${this.resourceUrl}${driverId}`, patchDictionary).pipe(map(response => Deserialize(response, Driver)));
  }

  /**
   * Deletes a driver's account.
   *
   * This action is irreversable!
   *
   * @param driverId
   * @permission driver
   */
  deleteAccount(driverId: number): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}${driverId}`).pipe(map(() => true));
  }

  /******************   Suspensions   ******************/

  /**
   * Returns a list of driver suspensions
   *
   * @param driverId
   * @param pageNumber
   * @param pageSize
   * @param includeInactive
   */
  getDriverSuspensions(driverId: number, pageNumber = 0, pageSize = 20, includeInactive = false): Observable<Array<DriverSuspension>> {
    return this.get<DriverSuspension>(`${this.resourceUrl}${driverId}/driverSuspensions/`, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      includeInactive: includeInactive
    }).pipe(map(response => Deserialize(response, DriverSuspension)));
  }

  /******************   Addresses   ******************/

  /**
   * Adds an address to a driver
   *
   * @param address
   * @param driver
   * @permission driver
   */
  addAddressToDriver(address: Address, driver: Driver): Observable<Driver> {
    return this.post<Driver>(`${this.resourceUrl}${driver.id}/addresses/`, Serialize(address)).pipe(
      map(response => Deserialize(response, Driver))
    );
  }

  /**
   * Gets a driver's address
   *
   * @param driver
   * @param addressId
   * @permission address_read
   */
  findDriverAddressById(driver: Driver, addressId: number): Observable<Address> {
    if (isNullOrUndefined(driver)) {
      return throwError(new ServiceException('Driver Cannot Be Null'));
    }

    if (isNullOrUndefined(addressId)) {
      return of(null);
      // Return throwError(new ServiceException('Driver Address ID Is Null'));
    }

    return this.get<Driver>(`${this.resourceUrl}${driver.id}/addresses/${addressId}`).pipe(map(response => Deserialize(response, Address)));
  }

  /*****************  Payout Account ***********************/

  /**
   * Gets a driver's payout account
   *
   * @param driverId
   */
  getDriverPayoutAccount(driverId: number): Observable<PaymentAccount> {
    return this.get<PaymentAccount>(`${this.resourceUrl}${driverId}/payoutAccount/`).pipe(
      map(response => Deserialize(response, PaymentAccount))
    );
  }

  /**
   * Saves a new driver payout account
   *
   * @param driverId
   * @param payoutAccount
   */
  saveDriverPayoutAccount(driverId: number, payoutAccount: PaymentAccount): Observable<PaymentAccount> {
    return this.post<PaymentAccount>(`${this.resourceUrl}${driverId}/payoutAccounts/`, Serialize(payoutAccount)).pipe(
      map(response => Deserialize(response, PaymentAccount))
    );
  }

  /**
   * Deletes a driver's payout account
   *
   * @param driverId
   */
  deleteDriverPayoutAccount(driverId: number): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}${driverId}/payoutAccount/`).pipe(map(() => true));
  }

  /**
   * Retrieves the pay statement of a driver within a specified date range.
   *
   * @param driverId
   * @param startDate
   * @param endDate
   * @permission driver
   */
  getPayStatement(driverId: number, startDate: Date, endDate: Date): Observable<FileData> {
    return this.get(`${this.resourceUrl}${driverId}/payStatement/`, {
      startDate: moment(startDate).format(DateFormatConstants.URL_DATE_FORMAT),
      endDate: moment(endDate).format(DateFormatConstants.URL_DATE_FORMAT)
    }).pipe(map(response => Deserialize(response, FileData)));
  }

  /**
   * Requests that the backend email a driver pay statement within a specified date range.
   *
   * @param driverId
   * @param startDate
   * @param endDate
   * @permission driver
   */
  sendDriverPayStatementEmail(driverId: number, startDate: Date, endDate: Date): Observable<boolean> {
    return this.get(`${this.resourceUrl}${driverId}/payStatement/email/`, {
      startDate: moment(startDate).format(DateFormatConstants.URL_DATE_FORMAT),
      endDate: moment(endDate).format(DateFormatConstants.URL_DATE_FORMAT)
    }).pipe(map(response => Deserialize(response, Boolean)));
  }

  /*****************  Insurance Policy *********************/

  /**
   * Gets the applicable insurance policy for a driver
   *
   * @param driverId
   * @permission insurance_read
   */
  findInsurancePolicyForDriver(driverId: number): Observable<InsurancePolicy> {
    return this.get(`${this.resourceUrl}${driverId}/insurancePolicy/`).pipe(
      map(response => {
        if (isNullOrUndefined(response)) {
          throw new ServiceException('Insurance Policy Not Found');
        }
        return Deserialize(response, InsurancePolicy);
      })
    );
  }

  /**
   * Gets the insurance policy for a reservation
   *
   * @param driverId
   * @param reservationId
   */
  getReservationInsurancePolicy(driverId: number, reservationId: number): Observable<InsurancePolicy> {
    return this.get(`drivers/${driverId}/reservations/${reservationId}/insurancePolicy/`).pipe(
      map(response => {
        if (isNullOrUndefined(response)) {
          throw new ServiceException('Insurance Policy Not Found');
        }
        return Deserialize(response, InsurancePolicy);
      })
    );
  }

  /***************** Journeys *********************/

  /**
   * Lists the feed journeys
   *
   * @param driverId
   * @param resultOffset
   * @param resultLimit
   * @param onDemand
   * @param date
   * @param startTime
   * @param endTime
   * @param zipCode
   * @permission driver
   */
  listJourneysInFeed(
    driverId: number,
    resultOffset: number,
    resultLimit: number,
    date?: Date,
    startTime?: Date,
    endTime?: Date,
    zipCode?: string
  ): Observable<PaginatedJourneyDetailList> {
    return this.get<Array<PaginatedJourneyDetailList>>(`${this.resourceUrl}${driverId}/journeys/`, {
      resultOffset: resultOffset,
      resultLimit: resultLimit,
      status: 'available',
      date: date ? moment(date).format(DateFormatConstants.URL_DATE_FORMAT) : null,
      startTime: startTime ? moment(startTime).format(DateFormatConstants.URL_TIME_FORMAT) : null,
      endTime: endTime ? moment(endTime).format(DateFormatConstants.URL_TIME_FORMAT) : null,
      zipCode: zipCode
    }).pipe(map(response => Deserialize(response, PaginatedJourneyDetailList)));
  }

  /**
   * Lists journeys the driver is interested in
   *
   * @param driverId
   * @param pageNumber
   * @param pageSize
   * @permission driver
   */
  listPendingJourneys(driverId: number, pageNumber: number, pageSize: number): Observable<Array<JourneyDetail>> {
    return this.get<Array<JourneyDetail>>(`${this.resourceUrl}${driverId}/journeys/`, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      status: 'pending'
    }).pipe(map(response => Deserialize(response, JourneyDetail)));
  }

  /**
   * Lists journeys the driver is assigned to
   *
   * @param driverId
   * @param pageNumber
   * @param pageSize
   * @permission driver
   */
  listScheduledJourneys(driverId: number, pageNumber: number, pageSize: number): Observable<Array<JourneyDetail>> {
    return this.get<Array<JourneyDetail>>(`${this.resourceUrl}${driverId}/journeys/`, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      status: 'scheduled'
    }).pipe(map(response => Deserialize(response, JourneyDetail)));
  }

  /**
   * Lists a single days journeys for a driver
   *
   * @param driverId
   * @param date
   * @permission driver
   */
  listRideCalendarJourneys(driverId: number, date: Date): Observable<Array<JourneyDetail>> {
    return this.get<Array<JourneyDetail>>(`${this.resourceUrl}${driverId}/journeys/`, {
      status: 'scheduled',
      date: moment(date).format(DateFormatConstants.URL_DATE_FORMAT)
    }).pipe(map(response => Deserialize(response, JourneyDetail)));
  }

  /**
   * Finds a journey by the given id
   *
   * @param driverId
   * @param journeyIdentifier
   * @param source
   * @permission driver
   */
  findJourneyByIdentifierAndSource(
    driverId: number,
    journeyIdentifier: number,
    uzurvDataSource: UzurvDataSource
  ): Observable<JourneyDetail> {
    return this.get<JourneyDetail>(`${this.resourceUrl}${driverId}/journeys/${journeyIdentifier}`, {
      source: uzurvDataSource
    }).pipe(map(response => Deserialize(response, JourneyDetail)));
  }

  /**
   * Lists ride requests for a given journey
   *
   * @param driverId
   * @param journeyIdentifier
   * @param source
   * @permission driver
   */
  listRideRequestsByJourneyIdentifierAndSource(
    driverId: number,
    journeyIdentifier: number,
    uzurvDataSource: UzurvDataSource
  ): Observable<Array<RideDetail>> {
    return this.get<Array<RideDetail>>(`${this.resourceUrl}${driverId}/journeys/${journeyIdentifier}/rideRequests/`, {
      source: uzurvDataSource
    }).pipe(map(response => Deserialize(response, RideDetail)));
  }

  /**
   * Lists reservation for a given journey
   *
   * @param driverId
   * @param journeyId
   * @permission driver
   */
  listReservationsByJourneyId(driverId: number, journeyId: number): Observable<Array<RideDetail>> {
    return this.get<Array<RideDetail>>(`${this.resourceUrl}${driverId}/journeys/${journeyId}/reservations/`).pipe(
      map(response => Deserialize(response, RideDetail))
    );
  }

  /***************** Interested Drivers *********************/

  /**
   * Get eligible Vehicles for a given RideRequest
   *
   * @param driverId
   * @param rideRequestId
   */
  getEligibleVehiclesForRideRequest(driverId: number, rideRequestId: number): Observable<Array<Vehicle>> {
    return this.get(`${this.resourceUrl}${driverId}/rideRequests/${rideRequestId}/eligibleVehicles/`).pipe(
      map(response => Deserialize(response, Vehicle))
    );
  }

  /**
   *
   * @param driver
   * @param rideRequestId
   * @param vehicleId
   * @param latitude
   * @param longitude
   * @param deviceInstallationId
   */
  addInterestedDriverToRideRequest(
    driver: Driver,
    rideRequestId: number,
    vehicleId: number,
    latitude: number,
    longitude: number,
    deviceInstallationId: number
  ): Observable<InterestedDriver> {
    const parameters = {
      vehicleId: vehicleId,
      latitude: latitude,
      longitude: longitude,
      deviceInstallationId: deviceInstallationId
    };

    return this.put(`${this.resourceUrl}${driver.id}/rideRequests/${rideRequestId}`, {}, parameters).pipe(
      map(response => Deserialize(response, InterestedDriver))
    );
  }

  /**
   * Removes an interested driver from a ride request
   *
   * @param driver
   * @param rideRequestId
   */
  removeInterestedDriverFromRideRequest(driver: Driver, rideRequestId: number): Observable<InterestedDriver> {
    return this.delete(`${this.resourceUrl}${driver.id}/rideRequests/${rideRequestId}`);
  }

  /**
   * Get eligible Vehicles for a given Journey
   *
   * @param driverId
   * @param medusaJourneyIdentifier
   */
  getEligibleVehiclesForJourney(driverId: number, medusaJourneyIdentifier: number): Observable<Array<Vehicle>> {
    return this.get(`${this.resourceUrl}${driverId}/journeys/${medusaJourneyIdentifier}/eligibleVehicles/`).pipe(
      map(response => Deserialize(response, Vehicle))
    );
  }

  /**
   *
   * @param driver
   * @param medusaJourneyIdentifier
   * @param vehicleId
   * @param latitude
   * @param longitude
   */
  addInterestedDriverToJourneyWithVehicle(
    driver: Driver,
    medusaJourneyIdentifier: number,
    vehicleId: number,
    latitude: number,
    longitude: number
  ): Observable<JourneyInterestedDriver> {
    const parameters = {
      vehicleId: vehicleId,
      latitude: latitude,
      longitude: longitude
    };

    return this.put(`${this.resourceUrl}${driver.id}/journeys/${medusaJourneyIdentifier}/interest/`, {}, parameters)
      .pipe(map(response => Deserialize(response, JourneyInterestedDriver)))
      .pipe(
        catchError(err => {
          const message: string = isNullOrUndefined(err.message) ? '' : err.message;

          // TODO: Add error constants for these

          // Map to translated errors
          if (message.includes('A Driver has already been chosen for this RideRequest')) {
            err.message = 'Error: A Driver has already been chosen for this RideRequest';
          } else if (message.includes('Drivers cannot express interest in their own ride request')) {
            err.message = 'Error: Drivers cannot express interest in their own ride request';
          } else if (message.includes(`The RideRequest overlaps one of Driver's existing Reservations`)) {
            err.message = `Error: The RideRequest overlaps one of Driver's existing Reservations`;
          } else if (message.includes('Driver has already expressed interest in this RideRequest')) {
            err.message = 'Error: Driver has already expressed interest in this RideRequest';
          } else if (message.includes('The RideRequest is no longer active')) {
            return throwError(new RideRequestNoLongerAvailableException('Error: The RideRequest is no longer active'));
          } else if (message.includes('This RideRequest is no longer available')) {
            return throwError(new RideRequestNoLongerAvailableException('Error: This RideRequest is no longer available'));
          }

          return throwError(err);
        })
      );
  }

  /**
   * Removes an interested driver from a ride request
   *
   * @param driver
   * @param medusaJourneyIdentifier
   */
  removeInterestedDriverFromJourney(driver: Driver, medusaJourneyIdentifier: number): Observable<boolean> {
    return this.delete(`${this.resourceUrl}${driver.id}/journeys/${medusaJourneyIdentifier}/interest/`);
  }

  // TODO: Remove these
  /***************** Ride Requests *********************/

  /**
   * Gets the feed ride requests
   *
   * @param driverId
   * @param pageNumber
   * @param pageSize
   * @param onDemand
   * @param date
   * @param startTime
   * @param endTime
   * @param zipCode
   * @permission driver
   */
  listRideRequestsInFeed(
    driverId: number,
    pageNumber: number,
    pageSize: number,
    onDemand?: boolean,
    date?: string,
    startTime?: string,
    endTime?: string,
    zipCode?: string
  ): Observable<Array<RideRequestFeedItem>> {
    return this.get<Array<RideRequestFeedItem>>(`${this.resourceUrl}${driverId}/rideRequests/`, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      onDemand: onDemand,
      date: date,
      startTime: startTime,
      endTime: endTime,
      zipCode: zipCode
    }).pipe(map(response => Deserialize(response, RideRequestFeedItem)));
  }

  /**
   * Returns a ride request by driverId and rideRequestId
   *
   * @param driverId
   * @param rideRequestId
   */
  findRideRequestById(driverId: number, rideRequestId: number): Observable<RideDetail> {
    return this.get<RideDetail>(`${this.resourceUrl}${driverId}/rideRequests/${rideRequestId}`).pipe(
      map(response => Deserialize(response, RideDetail))
    );
  }

  /**
   * Given a Ride Request ID, the method gets the rideRequestInfo from the rideRequestInfo service in
   * the form of a Google Maps Encoded Polyline.
   *
   * @param driverId
   * @param rideRequestId
   */
  getRideRequestInfoForRideRequestId(driverId: number, rideRequestId: number): Observable<RideRequestInfo> {
    return this.get<RideRequestInfo>(`${this.resourceUrl}${driverId}/rideRequests/${rideRequestId}/rideRequestInfo`).pipe(
      map(response => Deserialize(response, RideRequestInfo))
    );
  }

  /**
   * Returns a list of favorite riders for a driver
   *
   * @param id
   * @permission driver
   */
  listFavoriteRidersForDriverId(id: number): Observable<Array<MiniRider>> {
    return this.get(`${this.resourceUrl}${id}/riders/`, { pageNumber: 0, pageSize: 1000 }).pipe(
      map(response => Deserialize(response, MiniRider))
    );
  }

  /**
   * Returns a reservation by driverId and reservationId
   *
   * @param driverId
   * @param reservationId
   * @permission driver
   */
  findReservationById(driverId: number, reservationId: number): Observable<RideDetail> {
    return this.get<RideDetail>(`${this.resourceUrl}${driverId}/reservations/${reservationId}`).pipe(
      map(response => Deserialize(response, RideDetail))
    );
  }

  /**
   * Returns a reservation route summary by reservation ID
   *
   * @param driverId
   * @param reservationId
   * @param routePeriodCode
   */
  findReservationRouteSummaryByReservationId(
    driverId: number,
    reservationId: number,
    routePeriodCode: string
  ): Observable<ReservationRouteSummary> {
    return this.get<ReservationRouteSummary>(`${this.resourceUrl}${driverId}/reservations/${reservationId}/reservationRouteSummary/`, {
      routePeriodCode: routePeriodCode
    }).pipe(map(response => Deserialize(response, ReservationRouteSummary)));
  }

  /**
   * Updates a reservation with a new reservation status and geo-coordinates
   *
   * @param driverId
   * @param reservationId
   * @param reservationStatusName
   * @param latitude
   * @param longitude
   * @param deviceInstallationId
   */
  updateReservationStatus(
    driverId: number,
    reservationId: number,
    reservationStatusName: string,
    latitude: number,
    longitude: number,
    deviceInstallationId: number
  ): Observable<RideDetail> {
    const reservationStatusUpdate: ReservationStatusUpdate = new ReservationStatusUpdate();
    reservationStatusUpdate.reservationId = reservationId;
    reservationStatusUpdate.reservationStatusName = reservationStatusName;
    reservationStatusUpdate.timestamp = new Date();
    reservationStatusUpdate.latitude = latitude;
    reservationStatusUpdate.longitude = longitude;
    reservationStatusUpdate.deviceInstallationId = deviceInstallationId;

    return this.put<RideDetail>(
      `${this.resourceUrl}${driverId}/reservations/${reservationId}/reservationStatus/`,
      Serialize(reservationStatusUpdate)
    ).pipe(map(response => Deserialize(response, RideDetail)));
  }

  /**
   * Saves a list of queued route nodes
   *
   * @param driverId
   * @param routeNodes
   */
  saveQueuedRouteNodes(driverId: number, routeNodes: Array<OfflineRouteNode>) {
    return this.post<boolean>(`${this.resourceUrl}${driverId}/queuedRouteNodes/`, Serialize(routeNodes));
  }

  /**
   * Returns a list of reservations by driver ID
   *
   * @param driverId
   * @param pageNumber
   * @param pageSize
   * @param inactive
   * @param date
   * @param startTime
   * @param endTime
   */
  getReservationsByDriverId(
    driverId: number,
    pageNumber: number = 0,
    pageSize: number = 10,
    inactive: boolean = null,
    date?: string,
    startTime?: string,
    endTime?: string
  ): Observable<Array<RideDetail>> {
    const params = {
      pageNumber: pageNumber,
      pageSize: pageSize,
      date: date,
      startTime: startTime,
      endTime: endTime
    };

    if (!isNullOrUndefined(inactive)) {
      params['inactive'] = inactive;
    }

    return this.get<Array<RideDetail>>(`${this.resourceUrl}${driverId}/reservations/`, params).pipe(
      map(response => Deserialize(response, RideDetail))
    );
  }

  /**
   * Returns driver earnings by reservation ID
   *
   * @param driverId
   * @param reservationId
   */
  getDriverEarnings(driverId: number, reservationId: number): Observable<DriverEarnings> {
    return this.get<DriverEarnings>(`${this.resourceUrl}${driverId}/reservations/${reservationId}/driverEarnings/`)
      .pipe(map(response => Deserialize(response, DriverEarnings)))
      .pipe(
        catchError(err => {
          const message: string = isNullOrUndefined(err.message) ? '' : err.message;

          if (message.includes('FareCalculation for Reservation with id') && message.includes('not found')) {
            return throwError(new FareCalculationNotFoundException(message));
          }

          return throwError(err);
        })
      );
  }

  /**
   * Find payout account by driver id
   *
   * @param driverId
   */
  findPayoutAccountByDriverId(driverId: number): Observable<PaymentAccount> {
    return this.get<PaymentAccount>('drivers/' + driverId + '/payoutAccount/').pipe(map(response => Deserialize(response, PaymentAccount)));
  }

  /**
   * Gets a temporary generated phone number by reservation id
   *
   * @param driverId
   * @param reservationId
   */
  getGeneratedPhoneNumberByReservationId(driverId: number, reservationId: number): Observable<string> {
    return this.post<string>(`${this.resourceUrl}${driverId}/reservations/${reservationId}/phoneCalls/`, null);
  }

  /**
   * Cancels a reservation with a cancellation reason
   *
   * @param driverId
   * @param reservationId
   * @param cancellationReason
   */
  cancelReservationWithCancellationReason(
    driverId: number,
    reservationId: number,
    cancellationReason: CancellationReason
  ): Observable<Reservation> {
    const patchDictionary = {
      id: reservationId,
      cancelled: true,
      cancellationReason: cancellationReason
    };

    return this.patch<Reservation>(`${this.resourceUrl}${driverId}/reservations/${reservationId}`, patchDictionary).pipe(
      map(response => Deserialize(response, Reservation))
    );
  }

  /**
   * Cancels a journey with a cancellation reason
   *
   * @param driverId
   * @param journeyId
   * @param cancellationReason
   */
  cancelJourney(driverId: number, journeyId: number, cancellationReasonName: string): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}${driverId}/journeys/${journeyId}?cancellationReason=${cancellationReasonName}`).pipe(
      map(() => true)
    );
  }

  /*****************  Reservation Signatures ************/

  getReservationSignatures(driverId: number, reservationId: number): Observable<ReservationSignature> {
    return this.get<ReservationSignature>(`${this.resourceUrl}${driverId}/reservations/${reservationId}/signatures/`).pipe(
      map(response => Deserialize(response, ReservationSignature))
    );
  }

  getRiderSignature(driverId: number, reservationId: number): Observable<RiderSignature> {
    return this.get<RiderSignature>(`${this.resourceUrl}${driverId}/reservations/${reservationId}/signatures/rider/`).pipe(
      map(response => Deserialize(response, RiderSignature))
    );
  }

  saveReservationSignature(
    driverId: number,
    reservationId: number,
    reservationSignature: ReservationSignature
  ): Observable<ReservationSignature> {
    return this.post<ReservationSignature>(
      `${this.resourceUrl}${driverId}/reservations/${reservationId}/signatures/`,
      Serialize(reservationSignature)
    ).pipe(map(response => Deserialize(response, ReservationSignature)));
  }

  /*****************  Chat Messages *********************/

  /**
   * Returns the chat message list attached to a given reservation
   *
   * @param driverId
   * @param reservationId
   */
  getChatMessagesForReservation(driverId: number, reservationId: number): Observable<Array<ChatMessage>> {
    return this.get(`${this.resourceUrl}${driverId}/reservations/${reservationId}/chatMessages/`).pipe(
      map(response => Deserialize(response, ChatMessage))
    );
  }

  /**
   * Sends a chat message from the current logged in user to a provided user by ID, and is
   * attached to a provided reservation by ID. Authentication data will be cleared if it
   * can't be found
   *
   * @param driverId
   * @param chatMessage
   */
  sendChatMessage(driverId: number, chatMessage: ChatMessage): Observable<ChatMessage> {
    return this.post(`${this.resourceUrl}${driverId}/reservations/${chatMessage.reservationId}/chatMessages/`, Serialize(chatMessage)).pipe(
      map(response => Deserialize(response, ChatMessage))
    );
  }

  /**
   * Sends a chat message read receipt
   *
   * @param driverId
   * @param chatMessage
   */
  sendChatMessageReadReceipt(driverId: number, chatMessage: ChatMessage): Observable<ChatMessage> {
    if (chatMessage.read) {
      return of(chatMessage);
    }

    if (chatMessage.recipientPersonId !== driverId) {
      return of(chatMessage);
    }

    return this.patch(`${this.resourceUrl}${driverId}/reservations/${chatMessage.reservationId}/chatMessages/${chatMessage.id}`, {
      id: chatMessage.id,
      reservationId: chatMessage.reservationId,
      read: true
    }).pipe(
      map(response => {
        const chatMessage = Deserialize(response, ChatMessage);
        return chatMessage;
      })
    );
  }

  getVehicleWithIdForDriver(vehicleId: number, driver: Driver): Vehicle {
    return driver.vehicles.find(vehicle => vehicle.id === vehicleId);
  }

  // Toll Reimbursements

  /**
   * Gets a list of all pending toll reimbursements for the given driver id
   *
   * @param driverId
   */
  listPendingTollReimbursementsByDriverId(driverId: number): Observable<Array<TollReimbursement>> {
    return this.get(`${this.resourceUrl}${driverId}/tollReimbursements/`).pipe(map(response => Deserialize(response, TollReimbursement)));
  }

  /**
   * Submits a toll reimbursement for a driver
   *
   * @param driverId
   * @param tollReimbursement
   */
  submitTollReimbursement(driverId: number, tollReimbursement: TollReimbursement): Observable<TollReimbursement> {
    return this.put(`${this.resourceUrl}${driverId}/tollReimbursements/`, tollReimbursement, {
      reservationId: tollReimbursement.reservationId
    }).pipe(map(response => Deserialize(response, TollReimbursement)));
  }

  /**
   * Returns a list of rule values
   *
   */
  getDriverRuleValues(): Observable<Array<RuleValue>> {
    return this.get(`${this.resourceUrl}ruleValues/`, { pageNumber: 0, pageSize: 1000 }).pipe(
      map(response => Deserialize(response, RuleValue))
    );
  }

  /**
   * Returns a rule value by ID
   *
   * @param id
   */
  findDriverRuleValueWithId(id: number): Observable<RuleValue> {
    return this.getDriverRuleValues().pipe(map(response => response.find(ruleValue => ruleValue.id === id)));
  }

  /**
   * Returns a rule value by name
   *
   * @param name
   */
  findDriverRuleValueWithName(name: string): Observable<RuleValue> {
    return this.getDriverRuleValues().pipe(map(response => response.find(ruleValue => ruleValue.name === name)));
  }

  /************************************* Banners ***********************/

  /***
   * Lists banners for a driver
   *
   * @param driverId
   * @param appBundleIdentifier
   * @param appVersion
   * @param releasePlatformCode
   * @permission driver
   */
  listBannersByApp(
    driverId: number,
    appBundleIdentifier: string,
    appVersion: string,
    releasePlatformCode: string
  ): Observable<Array<Banner>> {
    return this.get<Array<Banner>>(`${this.resourceUrl}${driverId}/banners/`, {
      appBundleIdentifier: appBundleIdentifier,
      appVersion: appVersion,
      releasePlatformCode: releasePlatformCode
    }).pipe(map(response => this.bannerService.removeUserDismissedBanners(Deserialize(response, Banner))));
  }

  /**
   * Returns a list of cancellation reasons.
   *
   */
  listReservationCancellationReasons(): Observable<Array<CancellationReason>> {
    return this.get<Array<CancellationReason>>(`${this.resourceUrl}cancellationReasons/`).pipe(
      map(response => Deserialize(response, CancellationReason))
    );
  }

  /**
   * Returns a cancellation reason by ID
   *
   * @param id
   */
  findCancellationReasonWithId(id: number): Observable<CancellationReason> {
    return this.listReservationCancellationReasons().pipe(
      map(response => response.find(cancellationReason => cancellationReason.id === id))
    );
  }

  /**
   * Returns a cancellation reason by name
   *
   * @param name
   */
  findCancellationReasonWithName(name: string): Observable<CancellationReason> {
    return this.listReservationCancellationReasons().pipe(
      map(response => response.find(cancellationReason => cancellationReason.name === name))
    );
  }

  /************************************* Rider Position ***********************/

  getRiderPosition(driverId: number, reservationId: number): Observable<PositionUpdate> {
    return this.get<PositionUpdate>(`${this.resourceUrl}${driverId}/reservations/${reservationId}/riderPosition`).pipe(
      map(response => Deserialize(response, PositionUpdate))
    );
  }

  /************************************* Rider Payment Collection ***********************/

  listPaymentMethodsByReservationId(driverId: number, reservationId: number): Observable<Array<OrganizationPaymentMethod>> {
    return this.get<Array<OrganizationPaymentMethod>>(`${this.resourceUrl}${driverId}/reservations/${reservationId}/paymentMethods/`).pipe(
      map(response => Deserialize(response, OrganizationPaymentMethod))
    );
  }

  listRiderFaresByReservationId(driverId: number, reservationId: number): Observable<Array<RiderFare>> {
    return this.get<Array<RiderFare>>(`${this.resourceUrl}${driverId}/reservations/${reservationId}/riderFares/`).pipe(
      map(response => Deserialize(response, RiderFare))
    );
  }

  detectTextFromImage(id: number, image: FileData): Observable<Array<TextDetectionResult>> {
    return this.post(`${this.resourceUrl}${id}/detectTextFromImage/`, Serialize(image)).pipe(
      map(response => Deserialize(response, TextDetectionResult))
    );
  }

  saveRiderFare(driverId: number, reservationId: number, riderFare: RiderFare): Observable<RiderFare> {
    return this.post<RiderFare>(`${this.resourceUrl}${driverId}/reservations/${reservationId}/riderFares/`, Serialize(riderFare)).pipe(
      map(response => Deserialize(response, RiderFare))
    );
  }

  saveRiderFares(driverId: number, reservationId: number, riderFares: Array<RiderFare>): Observable<Array<RiderFare>> {
    return this.post<Array<RiderFare>>(
      `${this.resourceUrl}${driverId}/reservations/${reservationId}/riderFares/?multi=true`,
      Serialize(riderFares)
    ).pipe(map(response => Deserialize(response, RiderFare)));
  }

  /************************************* Vehicle Inspection ***********************/

  listTransitProgramVehicleInspectionItems(
    driverId: number,
    transitProgramId: number
  ): Observable<Array<TransitProgramVehicleInspectionItem>> {
    return this.get<Array<TransitProgramVehicleInspectionItem>>(
      `${this.resourceUrl}${driverId}/transitPrograms/${transitProgramId}/vehicleInspectionItems/`
    ).pipe(map(response => Deserialize(response, TransitProgramVehicleInspectionItem)));
  }

  saveVehicleInspection(driverId: number, vehicleInspection: VehicleInspection): Observable<VehicleInspection> {
    return this.post(
      `${this.resourceUrl}${driverId}/vehicles/${vehicleInspection.vehicleId}/inspections/`,
      Serialize(vehicleInspection)
    ).pipe(map(response => Deserialize(response, VehicleInspection)));
  }

  listAnnualRideExecutionDataByDriverId(driverId: number): Observable<Array<DriverAnnualRideExecutionData>> {
    return this.get(`${this.resourceUrl}${driverId}/annualRideExecutionData/`).pipe(
      map(response => Deserialize(response, DriverAnnualRideExecutionData))
    );
  }

  /************************************* TNC Licenses ***********************/

  getTNCLicensesForDriver(driverId: number): Observable<Array<TncLicense>> {
    return this.get(`${this.resourceUrl}${driverId}/tncLicenses/`).pipe(map(response => Deserialize(response, TncLicense)));
  }

  /************************************* Releases ***********************/

  findReleaseByAppBundleIdentifierReleasePlatformCodeAndVersion(
    driverId: number,
    appBundleIdentifier: string,
    releasePlatformCode: string,
    version: string
  ): Observable<Release> {
    return this.get<Array<Release>>(`${this.resourceUrl}${driverId}/releases/`, {
      appBundleIdentifier: appBundleIdentifier,
      releasePlatformCode: releasePlatformCode,
      version: version
    }).pipe(map(response => Deserialize(response, Release)));
  }
}
